import React from 'react'

import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import CircularProgress from '@material-ui/core/CircularProgress'
import Paper from '@material-ui/core/Paper'

import { CloseButton } from 'src/chrome'
import { useEffect, useRef, useState } from 'src/hooks'

import { SearchFilter } from '../types'
import FilterChip from '../lib/FilterChip'

import styles from '../filteredSearchBar.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

type Item = { id: string; name?: string }
type Props<T extends Item> = {
  filters: SearchFilter[]
  filterName: string
  deleteFilter: (filter: SearchFilter) => void
  onClear: () => void
  onCancel: () => void
  onSave?: (item: T) => void
  getKey: (item: T) => string
  getLabel: (item: T) => string
  autocomplete?: 'on' | 'off'
  setGroupId?: (id: string) => void
  setGroupName?: (name: string) => void
  data: T[]
  isLoading: boolean
  value: string
  setValue: (value: string) => void
}

const ListFilterInput = <T extends Item>(props: Props<T>) => {
  const {
    filterName,
    filters,
    deleteFilter,
    onClear,
    onCancel,
    onSave,
    getKey,
    getLabel,
    autocomplete,
    setGroupId,
    setGroupName,
    data,
    isLoading,
    setValue,
    value,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState<number>(-1)
  const definedFilters = filters.filter(filter => filter.value !== undefined)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    setHighlightIndex(-1)
  }, [data])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const { key } = e

    switch (key) {
      case 'Backspace':
        if (value === '') {
          onCancel()
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!data) return
        setHighlightIndex(x => ((x ?? -1) + 1 + data.length) % data.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!data) return
        setHighlightIndex(
          x => ((x ?? data.length) - 1 + data.length) % data.length,
        )
        break
      case 'Enter':
        if (!data) return
        if (highlightIndex !== -1) {
          if (onSave) {
            onSave(data[highlightIndex] as T)
          } else if (setGroupId && setGroupName) {
            setGroupId(data[highlightIndex].id)
            setGroupName(data[highlightIndex].name ?? '')
          }
        }
        break
    }
  }

  const handleSave = (item: T, index: number) => {
    if (typeof onSave === 'function') {
      onSave(item)
    } else if (
      typeof setGroupId === 'function' &&
      typeof setGroupName === 'function'
    ) {
      setGroupId(data[index].id)
      setGroupName(data[index].name ?? '')
    }
  }

  return (
    <>
      <div className={styles.filterChips}>
        {isFocused && value && (
          <div className={styles.filterTypeContainer}>
            <Paper style={{ width: 200 }}>
              {isLoading || !data ? (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: 300,
                    overflow: 'auto',
                    width: 200,
                  }}
                >
                  <List>
                    {data.length > 0 ? (
                      data.map((item, index) => (
                        <ListItem
                          id={`filtered_list_item_${index}`}
                          key={getKey(item)}
                          onMouseDown={() => {
                            handleSave(item, index)
                          }}
                          onTouchStart={() => {
                            handleSave(item, index)
                          }}
                          style={{
                            cursor: 'pointer',
                            background: index === highlightIndex ? '#eee' : '',
                          }}
                        >
                          <ListItemText primary={getLabel(item)} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary={'Not found'} />
                      </ListItem>
                    )}
                  </List>
                </div>
              )}
            </Paper>
          </div>
        )}

        <div className={styles.chips}>
          {filters.map(filter => (
            <FilterChip
              key={Math.random()}
              filter={filter}
              onDelete={deleteFilter}
            />
          ))}

          <Text
            type="caption"
            style={{
              padding: '2px 5px 2px 10px',
              backgroundColor: '#F7F7F7',
              borderRadius: 15,
            }}
          >
            {filterName}:
          </Text>
        </div>
      </div>

      <input
        id={'contact_search_bar'}
        style={{ height: 45 }}
        type="text"
        value={value}
        ref={inputRef}
        onChange={e => setValue(e.target.value)}
        className={styles.searchInput}
        placeholder="Type to search your contacts"
        onKeyDown={handleKeyDown}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        autoComplete={autocomplete}
      />

      {(definedFilters.length > 0 || value) && (
        <CloseButton onClose={() => onClear()} size={18} />
      )}
    </>
  )
}

export default ListFilterInput
